/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Download, FileText, Loader2 } from 'lucide-react';
import { useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { type ReportJob, type ReportJobStatus } from '../../lib/api/reports.js';
import { downloadReport } from '../../lib/queries/reports.js';
import { formatIst } from '../../lib/utils/datetime.js';

const STATUS_TONE: Readonly<Record<ReportJobStatus, BadgeTone>> = {
  queued: 'slate',
  running: 'violet',
  succeeded: 'green',
  failed: 'rose',
};

interface ReportRowProps {
  readonly job: ReportJob;
}

/** One report job as a Figma card — icon, title, date, type badge, live status + download. */
export function ReportRow({ job }: ReportRowProps): JSX.Element {
  const { t } = useTranslation();
  const [downloading, setDownloading] = useState(false);
  const pending = job.status === 'queued' || job.status === 'running';

  const onDownload = useCallback(() => {
    setDownloading(true);
    void downloadReport(job.id, job.fileName ?? 'report.pdf').finally(() => {
      setDownloading(false);
    });
  }, [job.id, job.fileName]);

  return (
    <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
      <span className="bg-[var(--accent)]/10 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-[var(--accent-strong)]">
        <FileText className="h-5 w-5" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-slate-800 dark:text-slate-100">{job.title}</p>
        <p className="text-xs text-slate-400">{formatIst(job.createdAt, 'dd MMM yyyy, h:mm a')}</p>
        {job.status === 'failed' && job.error !== null && (
          <p className="mt-1 text-xs text-rose-600">{job.error}</p>
        )}
      </div>
      <Badge label={t(`reports.type.${job.reportType}`)} tone="violet" />
      <span className="inline-flex items-center gap-2">
        {pending && (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--accent)]" aria-hidden="true" />
        )}
        <Badge label={t(`reports.status.${job.status}`)} tone={STATUS_TONE[job.status]} />
      </span>
      {job.status === 'succeeded' && (
        <Button variant="outline" size="sm" onClick={onDownload} disabled={downloading}>
          {downloading ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Download className="h-4 w-4" aria-hidden="true" />
          )}
          {t('reports.download')}
        </Button>
      )}
    </div>
  );
}
